<template>
  <div class="schedule-form">
    <!-- ========================================== -->
    <!-- حالت بارگذاری -->
    <!-- ========================================== -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>در حال بارگذاری...</span>
    </div>

    <div v-else>
      <!-- ========================================== -->
      <!-- مدت هر نوبت -->
      <!-- ========================================== -->
      <div class="form-group">
        <label class="required">مدت هر نوبت (دقیقه)</label>
        <input
            v-model.number="slotDuration"
            type="number"
            min="5"
            max="120"
            step="5"
            class="form-control"
        />
        <small class="help-text">
          این مدت برای همه روزهای فعال یکسان است.
        </small>
      </div>

      <!-- ========================================== -->
      <!-- روزهای هفته -->
      <!-- ========================================== -->
      <div class="days-section">
        <h4>📆 روزهای هفته</h4>

        <div
            v-for="day in schedules"
            :key="day.day_of_week"
            :class="['day-row', { active: day.is_active }]"
        >
          <div class="day-name">{{ day.day_label }}</div>

          <div class="day-toggle">
            <label class="switch">
              <input
                  type="checkbox"
                  v-model="day.is_active"
              />
              <span class="slider"></span>
            </label>
          </div>

          <div class="day-times" :class="{ disabled: !day.is_active }">
            <input
                v-model="day.start_time"
                type="time"
                class="time-input"
                :disabled="!day.is_active"
            />
            <span class="time-separator">تا</span>
            <input
                v-model="day.end_time"
                type="time"
                class="time-input"
                :disabled="!day.is_active"
            />
          </div>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- پر کردن خودکار -->
      <!-- ========================================== -->
      <div class="auto-fill-section">
        <h4>⚡ پر کردن خودکار هر شب</h4>

        <div class="form-group">
          <label>تا چند روز آینده خودکار پر شود؟</label>
          <input
              v-model.number="autoFillDays"
              type="number"
              min="0"
              max="60"
              class="form-control"
          />
          <small class="help-text">
            ۰ = غیرفعال (هیچ نوبتی خودکار ساخته نمی‌شود)
            <br>
            حداکثر ۶۰ روز
          </small>
        </div>

        <div v-if="autoFillDays > 0" class="info-box success">
          ✅ هر شب ساعت ۱۲، نوبت‌های {{ autoFillDays }} روز آینده به صورت خودکار ساخته می‌شود.
        </div>
        <div v-else class="info-box warning">
          ⚠️ پر کردن خودکار غیرفعال است.
        </div>
      </div>

      <!-- ========================================== -->
      <!-- دکمه ذخیره -->
      <!-- ========================================== -->
      <div class="form-actions">
        <button
            class="btn-primary"
            :disabled="saving"
            @click="save"
        >
          {{ saving ? '⏳ در حال ذخیره...' : '💾 ذخیره الگو' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'WorkScheduleForm',

  props: {
    adminId: {
      type: Number,
      required: true
    },
    lineId: {
      type: Number,
      required: true
    }
  },

  data: function() {
    return {
      loading: false,
      saving: false,
      slotDuration: 60,
      autoFillDays: 0,
      schedules: []
    };
  },

  mounted: function() {
    this.loadSchedules();
  },

  methods: {
    // ==========================================
    // بارگذاری الگو
    // ==========================================
    loadSchedules: async function() {
      this.loading = true;
      try {
        var formData = new FormData();
        formData.append('admin_id', this.adminId);
        formData.append('line_id', this.lineId);

        var response = await axios.post('/api/admin/work-schedules', formData);

        this.schedules = response.data.schedules || [];
        this.slotDuration = response.data.slot_duration || 30;
        this.autoFillDays = response.data.auto_fill_days || 0;

      } catch (error) {
        console.error('خطا در بارگذاری الگو:', error);
        this.showError('خطا در بارگذاری الگو');
      } finally {
        this.loading = false;
      }
    },

    // ==========================================
    // ذخیره
    // ==========================================
    save: async function() {
      // ==========================================
      // اعتبارسنجی
      // ==========================================
      var activeDays = this.schedules.filter(function(d) { return d.is_active; });

      if (activeDays.length === 0) {
        this.showError('حداقل یک روز باید فعال باشد.');
        return;
      }

      for (var i = 0; i < activeDays.length; i++) {
        if (activeDays[i].start_time >= activeDays[i].end_time) {
          this.showError('در ' + activeDays[i].day_label + '، ساعت شروع باید قبل از پایان باشد.');
          return;
        }
      }

      if (this.slotDuration < 5 || this.slotDuration > 120) {
        this.showError('مدت هر نوبت باید بین ۵ تا ۱۲۰ دقیقه باشد.');
        return;
      }

      if (this.autoFillDays < 0 || this.autoFillDays > 60) {
        this.showError('روزهای پر کردن خودکار باید بین ۰ تا ۶۰ باشد.');
        return;
      }

      // ==========================================
      // ارسال
      // ==========================================
      this.saving = true;
      try {
        var formData = new FormData();
        formData.append('admin_id', this.adminId);
        formData.append('line_id', this.lineId);
        formData.append('slot_duration', this.slotDuration);
        formData.append('auto_fill_days', this.autoFillDays);

        // ==========================================
        // ✅ هر روز جداگانه
        // ==========================================
        this.schedules.forEach(function(item, index) {
          formData.append('schedules[' + index + '][day_of_week]', item.day_of_week);
          formData.append('schedules[' + index + '][start_time]', item.start_time);
          formData.append('schedules[' + index + '][end_time]', item.end_time);
          formData.append('schedules[' + index + '][is_active]', item.is_active ? 1 : 0);
        });

        await axios.post('/api/admin/work-schedules/save', formData);

        this.$emit('saved');
        this.showSuccess('الگوی کاری ذخیره شد.');

      } catch (error) {
        var msg = 'خطا در ذخیره';
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message;
        }
        this.showError(msg);
      } finally {
        this.saving = false;
      }
    },
    showSuccess: function(msg) {
      if (this.$toast) this.$toast.success(msg);
      else alert(msg);
    },

    showError: function(msg) {
      if (this.$toast) this.$toast.error(msg);
      else alert(msg);
    }
  }
};
</script>

<style scoped>
.schedule-form {
  width: 100%;
}

/* ========================================== */
/* حالت بارگذاری */
/* ========================================== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  color: #909399;
  gap: 12px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #409eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ========================================== */
/* فرم */
/* ========================================== */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #2c3e50;
  margin-bottom: 8px;
}

.form-group label.required::after {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

.form-control {
  width: 100%;
  max-width: 200px;
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #409eff;
}

.help-text {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

/* ========================================== */
/* روزهای هفته */
/* ========================================== */
.days-section {
  margin: 24px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
}

.days-section h4 {
  margin: 0 0 16px 0;
  color: #2c3e50;
  font-size: 15px;
}

.day-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: white;
  border-radius: 8px;
  margin-bottom: 10px;
  border: 1px solid #e4e7ed;
  transition: all 0.2s;
}

.day-row.active {
  border-color: #409eff;
  background: #f7fbff;
}

.day-name {
  font-weight: 600;
  color: #2c3e50;
  min-width: 80px;
  font-size: 14px;
}

.day-toggle {
  flex-shrink: 0;
}

.day-times {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.day-times.disabled {
  opacity: 0.4;
}

.time-input {
  padding: 6px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  width: 110px;
}

.time-input:focus {
  outline: none;
  border-color: #409eff;
}

.time-separator {
  color: #909399;
  font-size: 13px;
}

/* ========================================== */
/* Switch */
/* ========================================== */
.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.switch input:checked + .slider {
  background-color: #409eff;
}

.switch input:checked + .slider:before {
  transform: translateX(20px);
}

/* ========================================== */
/* پر کردن خودکار */
/* ========================================== */
.auto-fill-section {
  margin: 24px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
}

.auto-fill-section h4 {
  margin: 0 0 16px 0;
  color: #2c3e50;
  font-size: 15px;
}

.info-box {
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;
  margin-top: 12px;
}

.info-box.success {
  background: #f0f9eb;
  border: 1px solid #c2e7b0;
  color: #67c23a;
}

.info-box.warning {
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  color: #e6a23c;
}

/* ========================================== */
/* دکمه‌ها */
/* ========================================== */
.form-actions {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
}

.btn-primary {
  padding: 12px 32px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #66b1ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.btn-primary:disabled {
  background: #a0cfff;
  cursor: not-allowed;
}

/* ========================================== */
/* ریسپانسیو */
/* ========================================== */
@media (max-width: 768px) {
  .day-row {
    flex-wrap: wrap;
  }

  .day-times {
    width: 100%;
    margin-top: 8px;
  }

  .time-input {
    flex: 1;
    width: auto;
  }

  .form-control {
    max-width: 100%;
  }
}

</style>